import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoaderCircle } from 'lucide-react';
import * as yup from 'yup';

import AuthLayout from '@/component-library/layouts/AuthLayout/AuthLayout';
import { Button } from '@/component-library/primitives/Button/Button';
import { Input } from '@/component-library/widgets/Input/Input';
import PasswordHints from '@/component-library/widgets/PasswordHints/PasswordHints';
import { PasswordData } from '@/models/PasswordData';
import { AppRoutes } from '@/utils/routes/router';

import { useAuth } from '../../hooks/useAuth';

const CreatePasswordPage = () => {
  const { newPassword } = useAuth();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const { t } = useTranslation();
  const [containsUppercase, setContainsUppercase] = useState(true);
  const [containsLowercase, setContainsLowercase] = useState(true);
  const [containsNumber, setContainsNumber] = useState(true);
  const [containsSpecialCharacter, setContainsSpecialCharacter] = useState(true);
  const [containsMinCharacters, setContainsMinCharacters] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const isPasswordValid =
    containsUppercase && containsLowercase && containsNumber && containsSpecialCharacter && containsMinCharacters;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const schema = useMemo(() => {
    return yup
      .object()
      .shape({
        password: yup
          .string()
          .required(t('CommonErrors.newPasswordRequired'))
          .matches(/[a-z]/, ' ')
          .matches(/[A-Z]/, ' ')
          .matches(/^.*(?=.*\d).*$/, ' ')
          .matches(/[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/, ' ')
          .min(8, ' ')
          .max(64, t('CommonErrors.passwordMaxCharacters')),
        confirmPassword: yup
          .string()
          .required(t('CommonErrors.confirmPasswordRequired'))
          .oneOf([yup.ref('password')], t('CommonErrors.passwordMismatch')),
      })
      .required();
  }, [t]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PasswordData>({
    resolver: yupResolver(schema),
  });

  const validatePassword = (password: string) => {
    // has uppercase letter
    if (password.toLowerCase() != password) setContainsUppercase(true);
    else setContainsUppercase(false);

    // has lowercase letter
    if (password.toUpperCase() != password) setContainsLowercase(true);
    else setContainsLowercase(false);

    // has number
    if (/\d/.test(password)) setContainsNumber(true);
    else setContainsNumber(false);

    // has special character
    if (/[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/.test(password)) setContainsSpecialCharacter(true);
    else setContainsSpecialCharacter(false);

    // has min 8 characters
    if (password.length >= 8) setContainsMinCharacters(true);
    else setContainsMinCharacters(false);
  };

  const onSubmit = async (data: PasswordData) => {
    setIsLoading(true);

    const { password } = data;
    try {
      await schema.validate(data, { abortEarly: false });
      const result = await newPassword(password);

      if (result.isSignInSuccess) {
        // User is now authenticated, redirect to dashboard
        navigate(AppRoutes.dashboard);
      } else {
        // If password creation failed, show error (shouldn't happen in normal flow)
        console.error('Password creation failed:', result.message);
      }
    } catch (error: any) {
      console.log('Something went wrong!', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title={t('CreatePasswordPage.title')} content={t('CreatePasswordPage.subtitle')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full space-y-6">
        <Input
          placeholder={t('CreatePasswordPage.newPasswordPlaceholder')}
          {...register('password', {
            onChange: (e) => validatePassword(e.target.value),
          })}
          error={errors.password?.message}
          type="password"
          label={t('CreatePasswordPage.newPasswordPlaceholder')}
          data-cy="newPassword"
        />
        {!isPasswordValid && (
          <PasswordHints
            constainsUppercase={containsUppercase}
            containsLowercase={containsLowercase}
            containsNumber={containsNumber}
            containsSpecialCharacter={containsSpecialCharacter}
            containsMinCharacters={containsMinCharacters}
          />
        )}
        <Input
          placeholder={t('CreatePasswordPage.confirmPasswordPlaceholder')}
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
          type="password"
          label={t('CreatePasswordPage.confirmPasswordPlaceholder')}
          data-cy="confirmPassword"
        />

        <Button
          className="w-full bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
          data-cy="buttonSetUpPassword"
          disabled={isLoading}
        >
          {t('CreatePasswordPage.buttonChangePassword')}
          {isLoading && <LoaderCircle height={20} className="animate-spin" />}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default CreatePasswordPage;
